import React, { useRef, useLayoutEffect } from 'react';
import { Columns } from '@components/Columns';
import { Box, BoxProps } from '@components/Box';
import { TextPairing } from '@components/TextPairing';
import { SpacingStack } from '@components/SpacingStack';
import { Circle, Toggle, RunTheShowWrapper } from './RunTheShow.styles';
import ScrollMagic from 'scrollmagic';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { useIsMounted } from '@hooks/useIsMounted';
import { gsap, Linear } from 'gsap';
import { useTheme } from '@styledComponents';
import ReactHtmlParser from 'react-html-parser';
import { useTranslation } from 'react-i18next';

interface RunTheShowProps {
  backgroundColor: BoxProps['backgroundColor'];
}

export const RunTheShow: React.FC<RunTheShowProps> = ({ backgroundColor }) => {
  const circleRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLDivElement>(null);
  const runTheShowContainerId = 'Real-time_RSVP_Controls';
  const runnTheShowContainerRef = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  const theme = useTheme();
  const { t } = useTranslation('onlineRsvp');
  const runTheShowData = t('runTheShow.columns');

  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let runTheShowTimeline: ReturnType<typeof gsap.timeline>;

    if (scrollController && isMounted) {
      const initTimeline = () => {
        if (scrollController) {
          runTheShowTimeline = gsap.timeline({ paused: true, defaults: { ease: Linear.easeNone } });
          runTheShowTimeline.fromTo(
            circleRef.current,
            {
              transform: 'translateX(0px)',
            },
            {
              transform: 'translateX(110px)',
              duration: 0.25,
            },
            0
          );
          runTheShowTimeline.to(toggleRef.current, { backgroundColor: theme.colors.blue4 }, 0);
          runTheShowTimeline.to(runnTheShowContainerRef.current, { backgroundColor: theme.colors.blue5 }, 0);
        }
      };
      const initScene = () => {
        scene = new ScrollMagic.Scene({
          triggerElement: `#${runTheShowContainerId}`,
          triggerHook: 'onCenter',
          duration: '100%',
        })
          .addTo(scrollController)
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            runTheShowTimeline?.progress(event.progress);
          });
      };
      initTimeline();
      initScene();
    }
    const clearScene = () => {
      if (runTheShowTimeline) {
        runTheShowTimeline.progress(0);
        runTheShowTimeline.kill();
      }
      if (scene) {
        scene.destroy(true);
      }
    };
    return clearScene;
  }, [scrollController, isMounted, theme]);

  return (
    <RunTheShowWrapper
      paddingTop={[10, 10, 13]}
      paddingBottom={[10, 10, 14]}
      paddingX={[5, 5, 11]}
      ref={runnTheShowContainerRef}
      id={runTheShowContainerId}
    >
      <SpacingStack spacing={[8, 8, 8]}>
        <Box style={{ maxWidth: '601px', alignSelf: 'center' }}>
          <SpacingStack spacing={[11]}>
            <Toggle ref={toggleRef}>
              <Circle ref={circleRef} />
            </Toggle>
            <TextPairing
              alignment="center"
              eyebrow={runTheShowData[0].eyebrow}
              eyebrowColor="blue3"
              eyebrowVariant="sectionEyeBrow24"
              eyebrowTagName="h2"
              title={ReactHtmlParser(runTheShowData[0].title)}
              titleMaxWidth="560px"
              titleVariant="hed96Break"
              titleColor="blue1"
              titleTagName="h2"
            />
          </SpacingStack>
        </Box>
        <Columns>
          <Columns.Column>
            <Box style={{ maxWidth: '485px', margin: 'auto' }}>
              <TextPairing
                alignment="center"
                title={runTheShowData[1].title}
                titleColor="blue1"
                titleVariant="sectionEyeBrow24"
                body={runTheShowData[1].content}
                bodyVariant="dek24"
                bodyColor="blue1"
                textAlign="center"
              />
            </Box>
          </Columns.Column>
        </Columns>
      </SpacingStack>
    </RunTheShowWrapper>
  );
};
